import React, { useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Globe,
    ChevronDown,
    Twitter,
    Linkedin,
    Youtube,
} from "lucide-react";
import footerConfig from "../config/footerConfig";
import { errorDev } from '../utils/logger.js';
import { getFormattedVersion, getShortVersion } from "../utils/version";

const SOCIAL_ICONS = {
    X: Twitter,
    LinkedIn: Linkedin,
    YouTube: Youtube,
};

function SocialLink({ platform, url, ariaLabel, showLabel = false }) {
    const Icon = SOCIAL_ICONS[platform];
    if (!Icon) return null;

    return (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={ariaLabel}
            className={`inline-flex items-center rounded-lg text-emerald-100/80 hover:text-emerald-100 hover:bg-emerald-500/15 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 focus:ring-offset-green-950 ${showLabel ? "gap-2 px-3 py-2 text-sm font-semibold" : "justify-center w-9 h-9"}`}
        >
            <Icon size={18} aria-hidden="true" strokeWidth={2} />
            {showLabel && <span>{platform}</span>}
        </a>
    );
}

function LanguageSelector() {
    const { languages } = footerConfig;
    const { i18n } = useTranslation();

    const handleChange = useCallback(
        (e) => {
            const code = e.target.value;
            i18n.changeLanguage(code);
            try {
                localStorage.setItem("language", code);
            } catch (err) {
                // silent
            errorDev("Operation failed:", err);
            }
        },
        [i18n]
    );

    return (
        <div className="flex items-center gap-1.5">
            <Globe
                size={14}
                className="text-emerald-200/70 shrink-0"
                aria-hidden="true"
            />
            <select
                value={(i18n.language || "en").split("-")[0]}
                onChange={handleChange}
                aria-label="Select language"
                className="bg-transparent text-xs text-emerald-100/80 border-none focus:outline-none focus:ring-0 cursor-pointer appearance-none pr-5"
            >
                {languages.map((lang) => (
                    <option
                        key={lang.code}
                        value={lang.code}
                        className="bg-green-950 text-emerald-100"
                    >
                        {lang.label}
                    </option>
                ))}
            </select>
        </div>
    );
}

function FooterLinkColumn({ heading, links }) {
    return (
        <div>
            <h3 className="text-sm font-semibold text-emerald-100 mb-3 tracking-wide uppercase">
                {heading}
            </h3>
            <ul className="space-y-2" role="list">
                {links.map((link) => (
                    <li key={link.path}>
                        <Link
                            to={link.path}
                            className="text-sm text-emerald-50/70 hover:text-emerald-100 transition-colors"
                        >
                            {link.label}
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function MobileAccordion({ heading, links }) {
    const [open, setOpen] = useState(false);

    return (
        <div className="border-b border-gray-200 dark:border-neutral-800">
            <button
                onClick={() => setOpen((prev) => !prev)}
                className="w-full flex items-center justify-between py-3 text-sm font-semibold text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500 rounded"
                aria-expanded={open}
            >
                {heading}
                <ChevronDown
                    size={16}
                    className={`text-gray-400 transition-transform duration-200 ${open ? "rotate-180" : ""
                        }`}
                    aria-hidden="true"
                />
            </button>
            {open && (
                <ul className="pb-3 space-y-2" role="list">
                    {links.map((link) => (
                        <li key={link.path}>
                            <Link
                                to={link.path}
                                className="block text-sm text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition-colors pl-1"
                            >
                                {link.label}
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export default function Footer() {
    const { brand, sections, legal, social, copyright } = footerConfig;
    const appVersion = getShortVersion();
    const formattedVersion = getFormattedVersion();

    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < 768);
        check();
        window.addEventListener("resize", check);
        return () => window.removeEventListener("resize", check);
    }, []);

    if (isMobile) {
        return null;
    }

    return (
        <footer
            className="relative overflow-hidden border-t border-emerald-700/40 bg-gradient-to-b from-green-900 via-green-950 to-neutral-950 transition-colors duration-300"
            role="contentinfo"
            aria-label="Site footer"
        >
            <div className="absolute inset-0 opacity-[0.05] pointer-events-none" aria-hidden="true">
                <svg width="100%" height="100%">
                    <defs>
                        <pattern id="footer-grid" width="26" height="26" patternUnits="userSpaceOnUse">
                            <path d="M 26 0 L 0 0 0 26" fill="none" stroke="white" strokeWidth="0.6" />
                        </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#footer-grid)" />
                </svg>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Desktop layout */}
                <div className="hidden md:block py-12 relative z-10">
                    <div className="grid grid-cols-12 gap-8">
                        {/* Brand section - redesigned */}
                        <div className="col-span-4">
                            <Link
                                to="/"
                                className="flex flex-col items-start group focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 focus:ring-offset-green-950 rounded-lg"
                                aria-label="Orrith home"
                            >
                                <div className="flex flex-col items-start gap-1 mb-3">
                                    <div className="flex items-center gap-2.5">
                                        <img
                                            src="/logo.png"
                                            alt=""
                                            className="w-12 h-12"
                                            aria-hidden="true"
                                        />
                                        <div className="text-center leading-none">
                                            <div className="text-[25px] font-extrabold tracking-[0.06em] text-emerald-100">
                                                {brand.name}
                                            </div>
                                            <div className="mt-0.5 text-[10px] uppercase tracking-[0.2em] text-emerald-200/90">
                                                {brand.tagline}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <p className="text-xs font-medium text-emerald-100/65 max-w-sm mb-4">
                                    {formattedVersion}
                                </p>
                                {brand.ecosystemLabel && (
                                    <p className="text-xs text-emerald-100/65 leading-relaxed max-w-sm mb-4">
                                        {brand.ecosystemLabel}
                                    </p>
                                )}
                                {/* Social links */}
                                <div className="flex items-center gap-2">
                                    {social.map((item) => (
                                        <SocialLink key={item.platform} {...item} showLabel />
                                    ))}
                                </div>
                            </Link>
                        </div>
                        {/* Link columns */}
                        <div className="col-span-8 grid grid-cols-3 gap-8">
                            {Object.values(sections).map((section) => (
                                <FooterLinkColumn
                                    key={section.heading}
                                    heading={section.heading}
                                    links={section.links}
                                />
                            ))}
                        </div>
                    </div>
                </div>

{/* Mobile layout */}
                <div className="md:hidden py-6">
                    {/* Brand section */}
                    <div className="flex flex-col items-start gap-1 mb-4">
                        <div className="flex items-center gap-2.5">
                            <img
                                src="/logo.png"
                                alt=""
                                className="w-10 h-10"
                                aria-hidden="true"
                            />
                            <div className="text-center leading-none">
                                <div className="text-[22px] font-extrabold tracking-[0.06em] text-emerald-100">
                                    {brand.name}
                                </div>
                                <div className="mt-0.5 text-[10px] uppercase tracking-[0.2em] text-emerald-200/90">
                                    {brand.tagline}
                                </div>
                            </div>
                        </div>
                    </div>
                    <p className="text-xs font-medium text-emerald-100/65 mb-4">
                        {formattedVersion}
                    </p>
                    {/* Accordion sections */}
                    <div>
                        {Object.values(sections).map((section) => (
                            <MobileAccordion
                                key={section.heading}
                                heading={section.heading}
                                links={section.links}
                            />
                        ))}
                    </div>
                    {/* Mobile social links */}
                    <div className="flex items-center gap-2 mt-4">
                        {social.map((item) => (
                            <SocialLink key={item.platform} {...item} />
                        ))}
                    </div>
                </div>

                {/* Bottom bar */}
                <div className="border-t border-emerald-700/35 py-5 relative z-10">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <p className="text-xs text-emerald-100/65">
                            {copyright}
                        </p>
                        <div className="flex items-center gap-4">
                            {/* Legal links */}
                            <nav className="flex items-center gap-4" aria-label="Legal">
                                {legal.map((item, idx) => (
                                    <React.Fragment key={item.path}>
                                        {idx > 0 && (
                                            <span
                                                className="text-emerald-200/25"
                                                aria-hidden="true"
                                            >
                                                {/* Separator space */}
                                            </span>
                                        )}
                                        <Link
                                            to={item.path}
                                            className="text-xs text-emerald-100/65 hover:text-emerald-100 transition-colors"
                                        >
                                            {item.label}
                                        </Link>
                                    </React.Fragment>
                                ))}
                            </nav>
                            {/* Language selector */}
                            <LanguageSelector />
                        </div>
                    </div>
                </div>
            </div>
            {/* Mobile spacer */}
            <div className="sm:hidden h-16" aria-hidden="true" />
        </footer>
    );
}